import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * The one styled menu in the app.
 *
 * Screens had been hand-rolling this — a `useState` boolean and an absolutely positioned
 * div — which meant no outside-click or Escape dismissal, no focus return and no arrow-key
 * navigation, and left the panel's edge pinned with a class that does not exist. Radix
 * owns that behaviour; this only dresses it in the app's tokens.
 */

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuGroup = DropdownMenuPrimitive.Group

export function DropdownMenuContent({
  className,
  sideOffset = 4,
  align = 'end',
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[11rem] max-h-[var(--radix-dropdown-menu-content-available-height)]',
          // A long menu scrolls inside itself and stops there, rather than passing the
          // remainder of the gesture to the page underneath.
          'overflow-y-auto overscroll-contain',
          'rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)]',
          'p-1 text-sm text-[var(--color-body)] shadow-[var(--shadow-popover)]',
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

const itemClasses =
  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-[6px] px-2 py-1.5 outline-none ' +
  'data-[highlighted]:bg-[var(--color-page)] data-[highlighted]:text-[var(--color-heading)] ' +
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'

export function DropdownMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return <DropdownMenuPrimitive.Item className={cn(itemClasses, className)} {...props} />
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={cn(itemClasses, 'pe-8', className)}
      {...props}
    >
      {children}
      <DropdownMenuPrimitive.ItemIndicator className="absolute end-2">
        <Check className="size-4 text-[var(--color-primary)]" aria-hidden="true" />
      </DropdownMenuPrimitive.ItemIndicator>
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

export function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      className={cn('px-2 py-1.5 text-xs font-medium text-[var(--color-muted)]', className)}
      {...props}
    />
  )
}

export function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn('my-1 h-px bg-[var(--color-border)]', className)}
      {...props}
    />
  )
}
